import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { StorageFacilityModal } from '../components/StorageFacilityModal';
import { storageFacilitiesApi } from '../inventoryApi';
import type { StorageFacilityListItem } from '../inventoryTypes';

/**
 * The places stock sits, and the stores nested inside them.
 *
 * **A hierarchy that is NOT a `<TreeChart>`**, and the reasoning is the one the Repository
 * already follows: that deviation was written for a chart of RANKS, where a record's position
 * is its most important field. A facility list is read to find somewhere to put something, so
 * it takes the ordinary list contract with a parent column.
 */
export function StorageFacilitiesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<StorageFacilityListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    facility: StorageFacilityListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchFacilities = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      storageFacilitiesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          type: query.facets.type?.join(','),
          unit: query.facets.unit?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (facility: StorageFacilityListItem): MenuItem[] => [
      {
        key: `edit-${facility.id}`,
        label: 'Edit',
        permission: 'storage-facilities.update',
        onClick: () => setEditing(facility),
      },
      {
        key: `restore-${facility.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'storage-facilities.delete',
        disabledReason: facility.isActive ? 'This facility is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', facility }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await storageFacilitiesApi.deactivate(confirming.facility.id);
      else await storageFacilitiesApi.activate(confirming.facility.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<StorageFacilityListItem>
        module="storage-facilities"
        // Reached through Inventory → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Logistics',
          'Inventory',
          { label: 'Settings', to: '/inventory/settings' },
          'Storage Facilities',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'type', label: 'Type' },
          // Another module's values, so the axis carries that module's key — the facility
          // reader's implied read is scoped to the write actions, so this genuinely hides.
          { key: 'unit', label: 'Department', permission: 'org-units.read' },
        ]}
        auditEntityTypes={['StorageFacility']}
        rowKey={(facility) => facility.id}
        fetch={fetchFacilities}
        reloadKey={reloadKey}
        searchPlaceholder="Search name, code, type or department"
        emptyMessage="No storage facilities yet. Add the places stock sits."
        onCreate={() => setCreating(true)}
        createPermission="storage-facilities.create"
        onRowOpen={(facility) => navigate(`/inventory/settings/storage-facilities/${facility.id}`)}
        openPermission="storage-facilities.read"
        rowActions={rowActions}
        onDelete={(facility) => setConfirming({ kind: 'deactivate', facility })}
        deletePermission="storage-facilities.delete"
        deleteLabel={() => 'Delete'}
        /*
          Disabled for STATE with the count named, rather than letting the reader click and meet
          a refusal. Deleting a parent and leaving its children live would strand them under a
          store no list shows; cascading instead would be a blast radius nobody asked for.
        */
        deleteDisabledReason={(facility) =>
          !facility.isActive
            ? 'This facility is already deleted.'
            : facility.childCount > 0
              ? `This facility holds ${facility.childCount} other ${
                  facility.childCount === 1 ? 'facility' : 'facilities'
                }. Move or delete them first.`
              : null
        }
        onClearData={() => storageFacilitiesApi.clear()}
        clearDataPermission="storage-facilities.clear-data"
        otherInfo={(facility) => facility}
      />

      {creating || editing !== null ? (
        <StorageFacilityModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete facility' : 'Restore facility'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`storage-facility-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.facility.name}" stops being offered as a place to put stock.`
                  : `"${confirming.facility.name}" is offered again as a place to put stock.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  It keeps its code, {confirming.facility.code}. A facility number is allocated
                  once and never reissued, so nothing quoting it becomes ambiguous — the call an
                  item&apos;s SKU already takes.
                </p>
              ) : (
                <p>
                  A restore is refused when another facility has taken the name under the same
                  parent meanwhile. The code is not re-checked: it was never freed.
                </p>
              )}

              <p>Nothing is erased — a deleted facility can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<StorageFacilityListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 240,
    flex: true,
    sortable: true,
    render: (facility) => <TextCell value={facility.name} />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 140,
    sortable: true,
    summary: true,
    render: (facility) => <TextCell value={facility.code} mono />,
  },
  {
    key: 'facilityType',
    header: 'Type',
    width: 150,
    sortable: true,
    summary: true,
    render: (facility) => <TextCell value={facility.facilityType} />,
  },
  {
    // What disambiguates two stores that share a name — "Shelf A" under two warehouses is two
    // real places, which is why the name is held once per PARENT rather than across the table.
    key: 'parent',
    header: 'Sits inside',
    width: 200,
    sortable: true,
    summary: true,
    render: (facility) => <TextCell value={facility.parentFacilityName} />,
  },
  {
    /*
      Gated, unlike the item list's category column. The distinction is the implied read: a
      facility reader's `org-units.read` is SCOPED to create and update, so a read-only reader
      genuinely does not hold it — and a gate that can actually hide something is the only kind
      worth writing.
    */
    key: 'unit',
    header: 'Department',
    width: 180,
    sortable: true,
    permission: 'org-units.read',
    render: (facility) => <TextCell value={facility.orgUnitName} />,
  },
  {
    key: 'childCount',
    header: 'Holds',
    width: 90,
    align: 'right',
    sortable: true,
    render: (facility) => <TextCell value={String(facility.childCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (facility) =>
      facility.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
